import React, { useState, useEffect, useMemo } from 'react';
import { devLog } from '../../../../utils/devLog';
import { track1 } from '../../../../utils/track1Telemetry';
import { Plus, PlusCircle, Search, Clock, Loader2, Settings, AlertTriangle, SlidersHorizontal, ClipboardCheck, Check, Wrench } from 'lucide-react';
// FT-358: middle-slot actions registered via context; the shell owns the bar.
import { useSetBottomBarActions } from '../../../../contexts/MobileBottomBarActionsContext';
import Dropdown from '../../../common/Dropdown';
import MaintenanceJobCardEnhanced from './MaintenanceJobCardEnhanced';
import MaintenanceSidebar from './MaintenanceSidebar';
import MaintenanceSummaryCard from './MaintenanceSummaryCard';
import MaintenanceCheckModal from './MaintenanceCheckModal';
import LogMaintenanceView from './LogMaintenanceView';
// FT-407: `import * as storage` removed — equipment register reads/writes now flow
// through useEquipmentRegister() (IDB), and no other storage.* call remained here.
// FT-48: Equipment-first generators REMOVED - jobs now come from manual entry only
// Centralized interval helpers
import { getJobFrequencyDays } from '../../../../utils/intervalHelpers';
import { getJobDisplayName } from '../../../../utils/maintenanceJobHelpers';
// View components removed - using grid view only
// import MaintenanceWeekView from './MaintenanceWeekView';
// import MaintenanceMonthView from './MaintenanceMonthView';
// import MaintenanceAllItems from './MaintenanceAllItems';
import AddCustomJobModal from './AddCustomJobModal';
import AddJobView from './AddJobView';
import MaintenanceHistoryView from './MaintenanceHistoryView';
import MaintenanceReportModal from './MaintenanceReportModal';
import MaintenanceSettingsView from './MaintenanceSettingsView';
import MaintenanceFilters from './MaintenanceFilters';
import MaintenanceViewToggle from './MaintenanceViewToggle';
// FT-446: dynamic category-filter options derived from the live jobs list.
import { buildMaintenanceCategoryOptions } from '../../../../utils/maintenanceCategoryFilterOptions';
import FilterTabs from '../../common/FilterTabs';
import { useVessel } from '../../../../contexts/VesselContext';
// FT-48: Auto-generation config imports REMOVED - manual maintenance only
import { useMaintenanceQuickActions } from '../../../../hooks/useMaintenanceQuickActions';
import { summarizeJob } from '../../../../services/equipmentMaintenanceService';
// BUG-005 related: Calculate equipment service status from job completions
import { calculateEquipmentServiceStatus } from '../../../../services/equipmentServiceCalculator';
// Official Logbook for auto-logging maintenance completions
import { useMainLogbook } from '../../../../hooks/domain/operations/useMainLogbook';
// FT-03/FT-04 FIX: Use correct domain hook for maintenance config (reads from 'maintenance_config' key)
import { useMaintenanceConfig } from '../../../../hooks/domain/maintenance/useMaintenanceConfig';
// FT-180: Single source of truth for maintenance stats
import { calculateMaintenanceStats } from './utils/maintenanceHelpers';
// FT-03 FIX OPTION A: Read from maintenance_history as fallback for lastCompleted
import { useMaintenanceHistory } from '../../../../hooks/domain/maintenance/useMaintenanceHistory';
// FT-182 v9: Domain hook for maintenance jobs - atomic updates, no race conditions
import { useMaintenanceJobsDomain } from '../../../../hooks/domain/maintenance/useMaintenanceJobsDomain';
// FT-407: Equipment Register migrated to IndexedDB — read via the IDB-backed hook.
// storage.readJSON() returned [] post-migration, hiding the Link-to-Equipment picker
// and breaking equipment-linked job stats/badges.
import { useEquipmentRegister } from '../../../../hooks/domain/useEquipmentRegister';
// FT-353f: cascade — clear defect's back-reference when a corrective job is archived
import { useDefects } from '../../../../hooks/domain/defects/useDefects';
// FT-267: ad-hoc maintenance logging — audit trail for completion events,
// crew + permissions for the modal. The maintenance_history facade is already
// imported above (useMaintenanceHistory at line 40); we reuse it for addCompletedCheck.
import { useAuditTrail } from '../../../../hooks/useAuditTrail';
import { useCrew } from '../../../../hooks/useCrew';
import { usePermissions } from '../../../../hooks/usePermissions';
// FT-267 §16: normalise the resolved category in audit moduleSpecificData so
// the audit trail captures the canonical code (handles legacy + free-text).
import { normaliseCategory } from '../../../../config/maintenanceRequirements-wrapper';

/**
 * FT-140 FIX: Parse date string as local time, avoiding timezone shift
 * new Date('2026-03-25') parses as UTC midnight, which can shift by ±1 day locally
 */
function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(dateStr);
}

const PlannedMaintenanceTab = ({
  onOpenCheck: externalOpenCheck,
  maintenanceHistory: passedMaintenanceHistory,
  currentVesselId: passedVesselId,
  categoryFilter: externalCategoryFilter,
  setCategoryFilter: externalSetCategoryFilter,
  onSyncRequirements,
  hasUpdates,
  checking,
  // FT-353d: passed through from MaintenanceModule so job cards can jump
  // back to the Defects module when the "→ DEF-..." chip is clicked.
  onNavigateToDefect
}) => {
  const { currentVesselId, getCurrentVessel } = useVessel();

  // FT-03/FT-04 FIX: Use domain hook to read from correct storage key ('maintenance_config')
  // Previously read from 'maintenance_settings' while MaintenanceSettingsView wrote to 'maintenance_config'
  const { config: maintenanceSettings } = useMaintenanceConfig();

  // FT-03 FIX OPTION A: Get maintenance_history for fallback lastCompleted lookup
  // Root cause: summarizeJob() only checks schedule.completions + equipment.serviceHistory
  // But completions are written to maintenance_history ONLY
  // FT-267: also pull addCompletedCheck so the ad-hoc save handler can write
  // directly into the completion ledger without spinning up another hook.
  const { history: maintenanceHistory, addCompletedCheck, signOffCompletion } = useMaintenanceHistory();

  // FT-267: ad-hoc maintenance support — crew dropdown, audit log, permission gate.
  const { crew } = useCrew();
  const { currentUser, can } = usePermissions();
  const { logChange: logMaintenanceAudit } = useAuditTrail('maintenance');

  // FT-394 FIX E: one call, one shape — matches every other can() in the codebase
  const canSignMaintenance = useMemo(
    () => can('sign', 'maintenance'),
    [can]
  );

  // FT-267: RBAC shim matches the canDo pattern used in useMaintenanceJobsDomain.
  // The new "Log Maintenance" affordance is a completion action, so it gates
  // on `maintenance.complete` — the same permission crew already has for
  // ticking off scheduled jobs.
  const canLogAdhocMaintenance = useMemo(() => {
    if (!can || typeof can !== 'function') return false;
    try {
      return (
        (can.length >= 2 && can('complete', 'maintenance')) ||
        can('maintenance:complete') ||
        can({ resource: 'maintenance', action: 'complete' }) ||
        can('maintenance', 'complete')
      );
    } catch {
      return false;
    }
  }, [can]);

  // Main Logbook hook for auto-logging maintenance completions
  const { logMaintenance } = useMainLogbook();

  // FT-182 v10: Domain hook is SINGLE SOURCE OF TRUTH for maintenance jobs
  // No more local useState - this eliminates dual state architecture that caused race conditions
  // The hook uses useStorageState internally which atomically updates localStorage + React state
  // list = active jobs only (already filtered by domain hook to exclude archived)
  const { list: maintenanceJobs, archive: archiveJob, loaded: jobsLoaded, add: domainAddJob, update: domainUpdateJob } = useMaintenanceJobsDomain();
  // FT-353f: cascade handler for defect back-reference on job delete/archive
  const { handleCorrectiveJobDeleted: cascadeDefectOnJobDelete } = useDefects();
  // FT-407: Equipment Register is now IDB-backed. Read via the hook (vessel-scoped
  // internally via useStorageState) instead of storage.readJSON(), which read
  // localStorage and returned [] after the IDB migration.
  // `equipment` is the hook's alias for the non-archived combined register.
  // The hook is reactive: it re-derives on vessel switch and on register writes,
  // replacing the old useState-initializer + FT-05 vessel-change useEffect.
  const { equipment, addItem: addEquipmentItem } = useEquipmentRegister();

  // Step 2: EXPERIMENTAL - Generate jobs in useMemo instead of useEffect
  // This runs synchronously during render, not after
  const generatedJobs = useMemo(() => {

    // NOTE: This is a placeholder - we'll move the actual generation logic here in Step 3
    // For now, just return empty array to establish the pattern
    return [];
  }, [equipment, currentVesselId]);

  // Step 2: Comparison logging
  useEffect(() => {



    if (maintenanceJobs.length !== generatedJobs.length) {

    } else if (generatedJobs.length > 0) {

    }
  }, [maintenanceJobs, generatedJobs]);

  // PERFORMANCE MONITORING - BUG-005 Complete Fix Step 1
  // Track render times to establish baseline and detect performance issues
  useEffect(() => {
    const startTime = performance.now();

    return () => {
      const endTime = performance.now();
      const duration = endTime - startTime;

      if (duration > 100) {

      }
    };
  });
  const [searchTerm, setSearchTerm] = useState('');
  // Use external category filter if provided, otherwise use local state
  const [localCategoryFilter, setLocalCategoryFilter] = useState('all');
  const categoryFilter = externalCategoryFilter !== undefined ? externalCategoryFilter : localCategoryFilter;
  const setCategoryFilter = externalSetCategoryFilter || setLocalCategoryFilter;
  const [statusFilter, setStatusFilter] = useState('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false); // FT-16: collapse filters on mobile
  const [selectedJob, setSelectedJob] = useState(null);
  const [showCheckModal, setShowCheckModal] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'week', 'month', 'all'
  const [timePeriod, setTimePeriod] = useState('all'); // '7days', '30days', 'all'
  const [showJobForm, setShowJobForm] = useState(false); // Full-screen job form
  const [showHistoryView, setShowHistoryView] = useState(false); // Full-screen history view
  // FT-267: ad-hoc maintenance view + transient feedback
  const [showLogMaintenanceView, setShowLogMaintenanceView] = useState(false);
  const [logMaintenanceError, setLogMaintenanceError] = useState(null);
  const [logMaintenanceToast, setLogMaintenanceToast] = useState(null);
  // Equipment filter (from Equipment Detail View "View Maintenance" button)
  const [equipmentFilter, setEquipmentFilter] = useState(null);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Check for equipment filter from sessionStorage (from Equipment Detail "View Maintenance" button)
  useEffect(() => {
    const filterData = sessionStorage.getItem('maintenanceEquipmentFilter');
    if (filterData) {
      try {
        const { equipmentId, equipmentName } = JSON.parse(filterData);
        setEquipmentFilter({ equipmentId, equipmentName });
        // Clear the sessionStorage so it doesn't persist on future visits
        sessionStorage.removeItem('maintenanceEquipmentFilter');
      } catch (e) {
        console.error('Failed to parse equipment filter from sessionStorage:', e);
        sessionStorage.removeItem('maintenanceEquipmentFilter');
      }
    }
  }, []);

  // Loading states for async operations
  const [isSavingJob, setIsSavingJob] = useState(false);
  const [isDeletingJob, setIsDeletingJob] = useState(false);
  const [isCompletingJob, setIsCompletingJob] = useState(false);
  // FT-394: sign-off state
  const [isSigningOffCompletion, setIsSigningOffCompletion] = useState(false);

  // BUG-005 FIX: Removed redundant useEffect that loaded equipment
  // Equipment is now loaded synchronously in useState (line 76-81)
  // This prevents race condition where useEffect ran AFTER normalizedJobs calculated

  // State for forcing refresh when vessel updates
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Listen for vessel updates to force refresh
  useEffect(() => {
    const handleVesselUpdate = (event) => {
      if (event.detail?.vesselId === currentVesselId) {
        // Force component to re-fetch vessel data
        setRefreshTrigger(prev => prev + 1);
      }
    };

    window.addEventListener('vesselUpdated', handleVesselUpdate);
    return () => window.removeEventListener('vesselUpdated', handleVesselUpdate);
  }, [currentVesselId]);

  // Simple listener for equipment updates - don't aggressively reload
  useEffect(() => {
    const handleEquipmentUpdate = (event) => {
      // Only log that we received the event, don't force reload
      if (event.detail?.vesselId === currentVesselId) {

      }
    };

    window.addEventListener('equipment:selections:saved', handleEquipmentUpdate);
    return () => {
      window.removeEventListener('equipment:selections:saved', handleEquipmentUpdate);
    };
  }, [currentVesselId]);

  // BUG-005 FIX: Listen for equipment updates from maintenance completion
  // This is CRITICAL - PlannedMaintenanceTab has isolated state that loads once on mount
  // Without this listener, equipment state never updates after job completion
  // BUG-005 FIX: Listen for sr:storage:set (the actual event fired) instead of sr:equipment:updated
  useEffect(() => {
    const handleStorageUpdated = (event) => {
      const vesselId = currentVesselId || passedVesselId;
      // Filter for updates relevant to maintenance
      // FIX (Rev 30): Also listen for maintenance_history updates!
      if (event.detail?.vesselId === vesselId) {
        const key = event.detail?.baseKey;

        // FT-407: the equipment_register branch was removed. `equipment` now comes
        // from useEquipmentRegister() (IDB-backed, reactive via useStorageState), so
        // register writes re-render this component automatically — no manual re-read.
        if (key === 'maintenance_history') {
          // If history updates (e.g. via Sync), we need to trigger a full data reload
          // We do this by toggling the refresh trigger
          setRefreshTrigger(prev => prev + 1);
        }
      }
    };

    window.addEventListener('sr:storage:set', handleStorageUpdated);
    return () => {
      window.removeEventListener('sr:storage:set', handleStorageUpdated);
    };
  }, [currentVesselId, passedVesselId]);

  // FT-182 v10: REMOVED sr:jobs:updated event listener
  // The domain hook (useMaintenanceJobsDomain) is now the SINGLE SOURCE OF TRUTH
  // It handles all storage reads/writes atomically via useStorageState
  // No need to manually sync - React re-renders when domain hook state changes

  // FT-182 v10: REMOVED loadMaintenanceData useEffect
  // Jobs now come from useMaintenanceJobsDomain hook (single source of truth)
  // The domain hook handles all storage reads/writes atomically

  // FT-182 v10: Equipment service status calculation (moved from old useEffect)
  // Calculate equipment service status from job completions when jobs or equipment change
  useEffect(() => {
    const vesselIdToUse = passedVesselId || currentVesselId;
    if (!vesselIdToUse || !maintenanceJobs) return;

    // Calculate equipment service status from job completions
    const equipmentWithStatus = calculateEquipmentServiceStatus(equipment, maintenanceJobs);

    // Emit event so Equipment Register can update its display
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sr:equipment:service:updated', {
        detail: {
          vesselId: vesselIdToUse,
          equipment: equipmentWithStatus
        }
      }));
    }

    // DEV ONLY: Debug helpers for maintenance jobs
    if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
      window.__lastMaintenanceJobs = maintenanceJobs;
      window.__allMaintenanceJobs = maintenanceJobs;
      window.dumpAllMaintenanceJobs = () => {
        console.table(maintenanceJobs.map(j => ({ id: j.id, title: j.title || j.item, cat: j.category })));
      };
    }
  }, [currentVesselId, passedVesselId, maintenanceJobs, equipment]);

  // REACTIVE NORMALIZATION: Convert raw jobs → normalized jobs with dates/status
  // BUG-005 FIX: This must be useMemo (not inside useEffect) for synchronous reactivity
  // When equipment updates via sr:equipment:updated, this recalculates in same render cycle
  // See BRIEF-20251203-BUG005-REACTIVE-FIX
  const normalizedJobs = React.useMemo(() => {
    const startTime = performance.now();

    if (!maintenanceJobs || maintenanceJobs.length === 0) {
      return [];
    }

    // Create equipment map for summarizeJob (same as card does)
    const equipmentMap = new Map(equipment.map(eq => [eq.id, eq]));

    // FT-03 FIX: Get advanceWarningDays from settings (default 30)
    const advanceWarningDays = maintenanceSettings?.advanceWarningDays || 30;

    const normalized = maintenanceJobs.map(job => {
      // FT-140: Pass maintenanceHistory to summarizeJob as SOURCE OF TRUTH
      // summarizeJob now uses history directly - no more separate fallback needed
      const summary = summarizeJob(job, equipmentMap, maintenanceHistory);

      // FT-140: lastCompleted now comes directly from summarizeJob (via maintenanceHistory)
      // The old fallback code is no longer needed since summarizeJob does it internally
      const lastCompleted = summary.lastCompleted;

      // Calculate nextDue and daysUntilDue
      let nextDue = null;
      let daysUntilDue = null;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (summary.nextDue) {
        // Has explicit nextDue from service history
        // FT-140: Use parseLocalDate to avoid timezone shift
        nextDue = parseLocalDate(summary.nextDue);
        nextDue.setHours(0, 0, 0, 0);
        daysUntilDue = Math.ceil((nextDue - today) / (1000 * 60 * 60 * 24));
      } else if (lastCompleted) {
        // Calculate nextDue from lastCompleted + interval
        // FT-140: Use parseLocalDate to avoid timezone shift
        const lastDate = parseLocalDate(lastCompleted);
        const intervalDays = getJobFrequencyDays(job);
        nextDue = new Date(lastDate);
        nextDue.setDate(nextDue.getDate() + intervalDays);
        nextDue.setHours(0, 0, 0, 0);
        daysUntilDue = Math.ceil((nextDue - today) / (1000 * 60 * 60 * 24));
      } else if (job.initialNextDue) {
        // Has initial due date set at job creation
        nextDue = parseLocalDate(job.initialNextDue);
        nextDue.setHours(0, 0, 0, 0);
        daysUntilDue = Math.ceil((nextDue - today) / (1000 * 60 * 60 * 24));
      } else {
        // Never completed AND no initial due date - due today
        nextDue = today;
        daysUntilDue = 0;
      }

      // Unified status logic - ALL jobs use daysUntilDue
      let status;
      let isOverdue = false;
      let isDueSoon = false;
      if (daysUntilDue < 0) {
        status = 'overdue';     // Past due → red
        isOverdue = true;
      } else if (daysUntilDue <= advanceWarningDays) {
        status = 'due_soon';    // Within threshold → yellow
        isDueSoon = true;
      } else {
        status = 'compliant';   // All good → green
      }

      const jobName = getJobDisplayName(job);

      // FT-140 DEBUG: Log detailed date calculation for comparison with Dashboard
      console.log('[FT-140 MAINTENANCE]', {
        jobName,
        jobId: job.id,
        summaryNextDue: summary.nextDue,
        summaryLastCompleted: summary.lastCompleted,
        lastCompletedUsed: lastCompleted,
        calculatedNextDue: nextDue?.toISOString?.()?.split('T')[0],
        daysUntilDue,
        status,
        isOverdue,
        isDueSoon,
        historyRecordsCount: maintenanceHistory?.length || 0
      });

      return {
        ...job,
        lastCompleted,
        nextDue,
        daysUntilDue,
        status
      };
    });

    // Performance monitoring - track normalization time
    const endTime = performance.now();
    const duration = endTime - startTime;

    if (duration > 50) {
      // console.warn optional
    }

    return normalized;
  }, [maintenanceJobs, equipment, maintenanceSettings?.advanceWarningDays, maintenanceHistory]); // FT-03: Recalculate when advanceWarningDays or history changes

  // FT-446: dynamic category-filter options — DISTINCT categories present on the
  // live active jobs (normalizedJobs), not a hardcoded list. Custom categories
  // appear; empty standard categories disappear. Derived from normalizedJobs (the
  // full active set) rather than filteredJobs, so the option set stays stable
  // while the user sub-filters by category/status/search.
  const categoryFilterOptions = React.useMemo(() => {
    const opts = buildMaintenanceCategoryOptions(normalizedJobs);
    // Keep the current selection visible even if its last job just left the list
    // (e.g. archived) — otherwise the dropdown would render a value with no
    // matching option and appear blank. 'all' is always present.
    if (
      categoryFilter &&
      categoryFilter !== 'all' &&
      !opts.some((o) => o.value === categoryFilter)
    ) {
      opts.push({
        value: categoryFilter,
        label: buildMaintenanceCategoryOptions([{ category: categoryFilter }])[1].label,
      });
    }
    return opts;
  }, [normalizedJobs, categoryFilter]);

  // FT-48: Removed maintenance_schedule_* write - this was auto-generated schedule
  // Jobs now come from manual entry only (maintenance_jobs_${vesselId})
  // See NOTICEBOARD FT-48 spec for rationale

  // Time period filtering - uses normalizedJobs for synchronous reactivity
  const filteredByTimePeriod = React.useMemo(() => {
    const now = new Date();
    const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    if (timePeriod === '7days') {
      return normalizedJobs.filter(job => {
        const dueDate = job.nextDue ? new Date(job.nextDue) : null;
        return dueDate && dueDate <= in7Days;
      });
    }
    if (timePeriod === '30days') {
      return normalizedJobs.filter(job => {
        const dueDate = job.nextDue ? new Date(job.nextDue) : null;
        return dueDate && dueDate <= in30Days;
      });
    }
    return normalizedJobs; // 'all'
  }, [normalizedJobs, timePeriod]);

  // Counts for tabs - uses normalizedJobs for synchronous reactivity
  const next7DaysCount = React.useMemo(() => {
    const now = new Date();
    const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return normalizedJobs.filter(job => {
      const dueDate = job.nextDue ? new Date(job.nextDue) : null;
      return dueDate && dueDate <= in7Days;
    }).length;
  }, [normalizedJobs]);

  const next30DaysCount = React.useMemo(() => {
    const now = new Date();
    const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    return normalizedJobs.filter(job => {
      const dueDate = job.nextDue ? new Date(job.nextDue) : null;
      return dueDate && dueDate <= in30Days;
    }).length;
  }, [normalizedJobs]);

  // Filter jobs based on search and filters (now uses time-filtered jobs)
  const filteredJobs = filteredByTimePeriod.filter(job => {
    // FT-189: Use getJobDisplayName for canonical name search
    const term = searchTerm.toLowerCase();
    const matchesSearch = searchTerm === '' ||
      getJobDisplayName(job).toLowerCase().includes(term) ||
      (job.checkType || '').toLowerCase().includes(term) ||
      (job.category || '').toLowerCase().includes(term) ||
      (job.regulation || '').toLowerCase().includes(term);
    const matchesCategory = categoryFilter === 'all' || job.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;

    // Equipment filter (from "View Maintenance" button on Equipment Detail)
    // FT-189: Use getJobDisplayName for canonical name matching
    const matchesEquipmentFilter = !equipmentFilter || (
      // Check linked equipment array
      (job.linkedEquipment && job.linkedEquipment.some(eq => eq.id === equipmentFilter.equipmentId)) ||
      // Also check equipmentId direct reference
      job.equipmentId === equipmentFilter.equipmentId ||
      // And equipment name match as fallback
      getJobDisplayName(job).toLowerCase().includes(equipmentFilter.equipmentName.toLowerCase())
    );

    // Settings-based filters: visibleCategories
    const matchesVisibleCategory = !maintenanceSettings?.visibleCategories?.length ||
      maintenanceSettings.visibleCategories.some(cat => {
        const jobCategory = (job.category || job.domain || '').toUpperCase();
        return jobCategory.includes(cat.toUpperCase());
      }) || job.custom || job.isCustom; // Allow custom jobs!

    // Settings-based filter: showCustomJobs
    const matchesCustomJobSetting = maintenanceSettings?.showCustomJobs !== false || !job.isCustom;

    return matchesSearch && matchesCategory && matchesStatus && matchesEquipmentFilter && matchesVisibleCategory && matchesCustomJobSetting;
  });

  // FT-187: Sort jobs by due date (overdue first, then due soon, then compliant, then no date)
  // This is the most natural order for operators checking what needs doing next
  const sortedJobs = useMemo(() => {
    const statusPriority = {
      'overdue': 0,    // Most urgent - show first
      'due_soon': 1,   // Next urgent
      'compliant': 2,  // Up to date
      'no_date': 3     // No due date - show last
    };

    return [...filteredJobs].sort((a, b) => {
      // Primary sort: by status priority
      const statusA = a.status || 'no_date';
      const statusB = b.status || 'no_date';
      const priorityA = statusPriority[statusA] ?? 3;
      const priorityB = statusPriority[statusB] ?? 3;

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // Secondary sort: by due date (soonest first within same status)
      const dateA = a.nextDue ? new Date(a.nextDue).getTime() : Infinity;
      const dateB = b.nextDue ? new Date(b.nextDue).getTime() : Infinity;

      if (dateA !== dateB) {
        return dateA - dateB;
      }

      // Tertiary sort: alphabetically by name for stability
      // FT-189: Use getJobDisplayName for canonical name sorting
      const nameA = getJobDisplayName(a).toLowerCase();
      const nameB = getJobDisplayName(b).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [filteredJobs]);

  // Pagination calculations (FT-187: now uses sortedJobs)
  const totalPages = Math.ceil(sortedJobs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedJobs = sortedJobs.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, statusFilter, timePeriod]);

  // Calculate stats - uses normalizedJobs for synchronous reactivity
  // FT-180: Uses shared calculateMaintenanceStats() - single source of truth with Dashboard
  const stats = calculateMaintenanceStats(normalizedJobs);

  const handleOpenCheck = (job) => {
    // If external handler provided (for full-screen view), use it
    if (externalOpenCheck) {
      externalOpenCheck(job);
    } else {
      // Otherwise use modal (fallback)
      setSelectedJob(job);
      setShowCheckModal(true);
    }
  };

  // Use quick actions hook (FT-182 v10: archiving via useMaintenanceJobsDomain)
  const {
    showAddCustomModal,
    setShowAddCustomModal,
    showReportModal,
    setShowReportModal,
    showSettingsModal,
    setShowSettingsModal,
    onQuickAction
  } = useMaintenanceQuickActions(filteredJobs, handleOpenCheck, currentVesselId, passedMaintenanceHistory);

  // FT-48: State for editing custom jobs
  const [editingJob, setEditingJob] = useState(null);

  // FT-48: Handle edit custom job
  const handleEditJob = (job) => {
    // Track 1: stamp editingJob with the current hydrationEpoch so save-time
    // can detect if the underlying maintenanceJobs array has been replaced
    // between modal-open and save (Investigator B's leading hypothesis).
    let stamped = job;
    try {
      stamped = Object.assign({}, job, {
        __track1_openEpoch: track1.readHydrationEpoch(),
        __track1_openedAt: Date.now(),
      });
    } catch (_) {
      stamped = job;
    }
    setEditingJob(stamped);
    setShowAddCustomModal(true);
  };

  // FT-48: Handle modal close - clear editing state
  const handleCloseAddModal = () => {
    setEditingJob(null);
    setShowAddCustomModal(false);
  };

  const handleViewHistory = () => {
    setShowHistoryView(true);
  };

  // FT-267: ad-hoc maintenance save handler. Writes to maintenance_history via
  // addCompletedCheck (which sets completionType: 'adhoc' end-to-end) and fires
  // a fire-and-forget audit entry. Errors in addCompletedCheck keep the modal
  // open with an inline error; audit failures are swallowed per the
  // VesselSettingsView precedent.
  const handleLogAdhocMaintenance = async (payload) => {
    setLogMaintenanceError(null);

    // FT-267 §15: resolve equipment names at write time (FT-221 pattern for equipment).
    // `equipment` is the vessel equipment register loaded in this component's state.
    const resolvedEquipmentNames = (payload.equipmentIds || [])
      .map(id => {
        const eq = equipment.find(e => e.id === id);
        return eq?.name || eq?.item || eq?.label || null;
      })
      .filter(Boolean);

    let historyId;
    try {
      historyId = addCompletedCheck({
        fields: {
          completionType: 'adhoc',
          adhocItem: payload.adhocItem,
          // CRITICAL: mirror into title so the existing history view title
          // resolution chain (record.title || record.checkType || record.equipmentType)
          // renders the right label without any view-side changes.
          title: payload.adhocItem,
          category: payload.category,
          date: payload.date,
          notes: payload.notes,
          hoursSpent: payload.hoursSpent,
          jobId: null,
        },
        performer: payload.performedBy,
        performerName: payload.performerName,
        equipmentIds: payload.equipmentIds,
        equipmentNames: resolvedEquipmentNames,  // FT-267 §15: stored at write time, FT-221 pattern
      });
    } catch (err) {
      console.error('[LogMaintenance] addCompletedCheck failed:', err);
      setLogMaintenanceError('Failed to save maintenance log. Please try again.');
      return;
    }

    if (!historyId) {
      console.error('[LogMaintenance] addCompletedCheck returned no id');
      setLogMaintenanceError('Failed to save maintenance log. Please try again.');
      return;
    }

    // Fire-and-forget audit write — failure must not block the success path.
    logMaintenanceAudit({
      action: 'maintenance_logged_adhoc',
      item: {
        id: historyId,
        type: 'maintenance_completion',
        name: payload.adhocItem,
        category: payload.category,
      },
      reason: { code: 'logged_adhoc', label: 'Ad-hoc maintenance logged' },
      moduleSpecificData: {
        completionType: 'adhoc',
        hoursSpent: payload.hoursSpent,
        equipmentIds: payload.equipmentIds,
        equipmentNames: resolvedEquipmentNames,  // FT-267 §15: audit-trail stability, FT-221 pattern
        // FT-267 §16: canonical category code in the audit log — handles
        // legacy-shape and free-text custom values consistently.
        category: normaliseCategory(payload.category),
        date: payload.date,
        performedBy: payload.performedBy,
        performerName: payload.performerName,
      },
    }).catch(err => console.warn('[LogMaintenance] audit write failed (non-fatal):', err));

    setShowLogMaintenanceView(false);
    setLogMaintenanceToast({ type: 'success', message: 'Maintenance logged.' });
    setTimeout(() => setLogMaintenanceToast(null), 3000);
  };

  // Handle maintenance job completion - logs to Official Logbook
  const handleSaveCompletion = async (completionData) => {
    setIsCompletingJob(true);
    try {
      // Log maintenance completion to Official Logbook
      if (selectedJob) {
        await logMaintenance({
          jobId: selectedJob.id,
          jobName: selectedJob.title || selectedJob.name || selectedJob.equipmentType,
          equipment: selectedJob.equipmentType || selectedJob.category,
          completedBy: completionData.inspector,
          completedAt: completionData.completedAt || new Date().toISOString(),
          timestamp: new Date().toISOString(),
          notes: completionData.notes,
          defectsFound: completionData.defectsFound,
          defectDetails: completionData.defectDetails
        });
      }

      setShowCheckModal(false);
      setSelectedJob(null);
    } catch (error) {
      console.error('[PlannedMaintenanceTab] Error saving completion:', error);
    } finally {
      setIsCompletingJob(false);
    }
  };


  // FT-394: Sign off a maintenance completion record (skipper+ only)
  const handleSignOffCompletion = (recordId) => {
    if (!canSignMaintenance) return false;
    setIsSigningOffCompletion(true);
    try {
      return signOffCompletion(recordId);
    } finally {
      setIsSigningOffCompletion(false);
    }
  };

  // Render content based on view mode
  const renderContent = () => {
    // For now, all view modes show the grid view until view components are re-implemented
    // TODO: Re-implement week, month, and table views
    return filteredJobs.length === 0 ? (
      <div className="px-6 py-12 text-center text-gray-500">
        {currentVesselId ?
          'No maintenance jobs found matching your filters.' :
          'Please select a vessel to view maintenance schedule.'
        }
      </div>
    ) : (
      <div className="grid gap-4">
        {paginatedJobs.map((job, jobIndex) => (
          <MaintenanceJobCardEnhanced
            key={job.id || `maintenance-job-${jobIndex}`}
            job={job}
            onComplete={() => handleOpenCheck(job)}
            onViewHistory={handleViewHistory}
            onViewDetail={() => handleOpenCheck(job)}
            onClick={() => handleOpenCheck(job)}
            // FT-186: onEdit removed - editing is now in detail view modal
            // FT-353d: propagate defect nav so the origin chip can jump back.
            onNavigateToDefect={onNavigateToDefect}
          />
        ))}
      </div>
    );
  };

  // Render pagination controls
  const renderPagination = () => {
    if (filteredJobs.length <= itemsPerPage) return null;

    return (
      <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
        {/* Items per page selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">Show:</span>
          <Dropdown
            value={itemsPerPage}
            onChange={(val) => {
              setItemsPerPage(Number(val));
              setCurrentPage(1);
            }}
            width="w-20"
            options={[
              { value: 10, label: '10' },
              { value: 20, label: '20' },
              { value: 50, label: '50' },
              { value: filteredJobs.length, label: 'All' },
            ]}
          />
          <span className="text-sm text-gray-700">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredJobs.length)} of {filteredJobs.length}
          </span>
        </div>

        {/* Page navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>

          {/* Page numbers */}
          <div className="flex gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => {
                // Show first, last, current, and 2 either side of current
                return page === 1 ||
                  page === totalPages ||
                  Math.abs(page - currentPage) <= 2;
              })
              .map((page, idx, arr) => {
                // Add ellipsis if gap
                const prevPage = arr[idx - 1];
                const showEllipsis = prevPage && page - prevPage > 1;

                return (
                  <React.Fragment key={page}>
                    {showEllipsis && <span className="px-2 text-gray-400">...</span>}
                    <button
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 text-sm rounded-md ${currentPage === page
                        ? 'bg-cyan-600 text-white'
                        : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                );
              })}
          </div>

          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Maintenance Debug Panel component
  // MaintenanceDebugPanel - REMOVED for stability
  // const MaintenanceDebugPanel = ({ jobs, equipment }) => { ... };

  // FT-358: register middle-slot bottom-bar actions per brief §6. Trim
  // 4-action bar down to 3 by dropping Export (Export lives on the
  // Completion Report screen instead).
  const isSubView = showJobForm || showLogMaintenanceView || showHistoryView;
  useSetBottomBarActions(
    isSubView
      ? []
      : [
          {
            key: 'maint-add-job',
            icon: PlusCircle,
            label: 'Add Job',
            primary: true,
            onClick: () => setShowJobForm(true),
          },
          ...(canLogAdhocMaintenance
            ? [
                {
                  key: 'maint-log',
                  icon: Wrench,
                  label: 'Log Maint.',
                  onClick: () => setShowLogMaintenanceView(true),
                },
              ]
            : []),
          {
            key: 'maint-history',
            icon: Clock,
            label: 'History',
            onClick: () => setShowHistoryView(true),
          },
        ],
  );

  // Show full-screen job form if active
  if (showJobForm) {
    return (
      <AddJobView
        onBack={() => setShowJobForm(false)}
        onSave={async (jobData) => {
          setIsSavingJob(true);
          try {
            domainAddJob({ ...jobData, custom: true });
            setShowJobForm(false);
          } finally {
            setIsSavingJob(false);
          }
        }}
        isSaving={isSavingJob}
      />
    );
  }

  // FT-267: Show full-screen log-maintenance view if active. Matches
  // AddJobView's early-return pattern so the visual shell is consistent
  // across the two creation flows.
  if (showLogMaintenanceView) {
    return (
      <LogMaintenanceView
        onBack={() => {
          setShowLogMaintenanceView(false);
          setLogMaintenanceError(null);
        }}
        onSave={handleLogAdhocMaintenance}
        equipment={equipment}
        crew={crew}
        currentUser={currentUser}
        saveError={logMaintenanceError}
      />
    );
  }

  // Show full-screen history view if active
  if (showHistoryView) {
    return (
      <MaintenanceHistoryView
        onBack={() => setShowHistoryView(false)}
      />
    );
  }

  // Show full-screen settings view if active
  if (showSettingsModal) {
    return (
      <MaintenanceSettingsView
        onBack={() => setShowSettingsModal(false)}
      />
    );
  }

  // FT-52: Show empty state when no maintenance jobs exist (not based on equipment)
  if (!maintenanceJobs || maintenanceJobs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6">
        {/* Compliance Warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 max-w-md">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-amber-900">Compliance Notice</h4>
              <p className="text-sm text-amber-800 mt-1">
                Regular maintenance is a legal requirement for all commercial vessels.
                Use the 'Add Job' button to create maintenance schedules.
              </p>
            </div>
          </div>
        </div>

        {/* Empty State */}
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
          <Settings className="h-8 w-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No Maintenance Jobs
        </h3>
        <p className="text-gray-500 text-center mb-6 max-w-md">
          Create maintenance jobs to track your vessel's maintenance schedule.
          Start from a template or create custom jobs.
        </p>
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <button
            onClick={() => setShowJobForm(true)}
            className="inline-flex items-center px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Job
          </button>
          {canLogAdhocMaintenance && (
            <button
              onClick={() => {
                setLogMaintenanceError(null);
                setShowLogMaintenanceView(true);
              }}
              className="inline-flex items-center px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ClipboardCheck className="h-4 w-4 mr-2" />
              Log Maintenance
            </button>
          )}
        </div>

        {/* FT-267: rendering of LogMaintenanceView happens via the early-return
            block above (mirrors AddJobView). No modal needed here — only the
            success toast remains so it can render after the view closes. */}

        {/* FT-267: success toast (shared pattern with MaintenanceSettingsView) */}
        {logMaintenanceToast && (
          <div
            className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
              logMaintenanceToast.type === 'success'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            <div className="flex items-center gap-2">
              {logMaintenanceToast.type === 'success' ? (
                <Check className="h-4 w-4" />
              ) : (
                <AlertTriangle className="h-4 w-4" />
              )}
              {logMaintenanceToast.message}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {/* Maintenance Debug Panel - REMOVED */}

      {/* Standard 2/3 + 1/3 Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content - 2/3 width */}
        <div className="lg:col-span-2 pb-20 lg:pb-0">
          <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            {/* Main Header with Search */}
            <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
              <div className="flex items-center justify-between gap-4 sm:gap-6">
                {/* Title - Responsive size */}
                <h3 className="text-base font-semibold text-white whitespace-nowrap flex-shrink-0">
                  Maintenance<span className="hidden sm:inline"> Schedule</span>
                </h3>

                {/* Search Input - hidden on mobile, shown on desktop */}
                <div className="relative flex-1 max-w-2xl hidden sm:block">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search equipment or check type..."
                    aria-label="Search maintenance tasks by equipment or job name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-white text-slate-800 text-sm border border-white/20 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200"
                  />
                </div>

                {/* Mobile: Filter toggle button */}
                <button
                  onClick={() => setShowMobileFilters(!showMobileFilters)}
                  className="sm:hidden flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-md text-sm font-medium transition-colors"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  Filter
                  {(categoryFilter !== 'all' || statusFilter !== 'all' || searchTerm) && (
                    <span className="h-2 w-2 bg-cyan-400 rounded-full" />
                  )}
                </button>
              </div>
            </div>

            {/* Equipment Filter Banner (shown when filtering by specific equipment) */}
            {equipmentFilter && (
              <div className="px-6 py-3 bg-cyan-50 border-b border-cyan-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-cyan-900">
                    Showing maintenance for: <strong>{equipmentFilter.equipmentName}</strong>
                  </span>
                  <button
                    onClick={() => setEquipmentFilter(null)}
                    className="text-sm text-cyan-600 hover:text-cyan-800 font-medium"
                  >
                    Clear filter
                  </button>
                </div>
              </div>
            )}

            {/* Content Section */}
            <div className="px-4 sm:px-6 py-3 sm:py-5">
              <div className="space-y-3">
                {/* Mobile: Collapsible filter panel (search + dropdowns) */}
                {showMobileFilters && (
                  <div className="sm:hidden space-y-3 pb-2">
                    {/* Mobile search */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      <input
                        type="text"
                        placeholder="Search equipment..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-3 py-2.5 bg-white text-slate-800 text-sm border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                    {/* Mobile dropdowns */}
                    <div className="flex gap-3">
                      <Dropdown
                        value={categoryFilter}
                        onChange={setCategoryFilter}
                        width="flex-1"
                        options={categoryFilterOptions}
                      />
                      <Dropdown
                        value={statusFilter}
                        onChange={setStatusFilter}
                        width="flex-1"
                        options={[
                          { value: 'all', label: 'All Status' },
                          { value: 'overdue', label: 'Overdue' },
                          { value: 'due_soon', label: 'Due Soon' },
                          { value: 'compliant', label: 'Compliant' },
                        ]}
                      />
                    </div>
                  </div>
                )}

                {/* Desktop: Always-visible filter dropdowns */}
                <div className="hidden sm:flex gap-3">
                  {/* Category Filter — FT-446: dynamic from live jobs */}
                  <Dropdown
                    value={categoryFilter}
                    onChange={setCategoryFilter}
                    width="flex-none w-40"
                    options={categoryFilterOptions}
                  />

                  {/* Status Filter */}
                  <Dropdown
                    value={statusFilter}
                    onChange={setStatusFilter}
                    width="flex-none w-36"
                    options={[
                      { value: 'all', label: 'All Status' },
                      { value: 'overdue', label: 'Overdue' },
                      { value: 'due_soon', label: 'Due Soon' },
                      { value: 'compliant', label: 'Compliant' },
                    ]}
                  />
                </div>

                {/* Time period tabs + Count - always visible */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1">
                    <FilterTabs
                      tabs={[
                        { id: '7days', label: 'Next 7 Days', count: next7DaysCount },
                        { id: '30days', label: 'Next 30 Days', count: next30DaysCount },
                        { id: 'all', label: 'All', count: normalizedJobs.length }
                      ]}
                      activeTab={timePeriod}
                      onTabChange={setTimePeriod}
                    />
                  </div>
                  <span className="text-sm text-gray-500 whitespace-nowrap hidden sm:inline">{normalizedJobs.length} jobs</span>
                </div>
              </div>
            </div>

            {/* Render content based on view mode */}
            <div className="p-6">
              {renderContent()}
              {renderPagination()}
            </div>
          </div>
        </div>

        {/* Sidebar - 1/3 width - Hidden on mobile */}
        <div className="hidden lg:block">
          <MaintenanceSidebar
            stats={stats}
            upcomingJobs={normalizedJobs.filter(j => j.status === 'due_soon').slice(0, 5)}
            canLogAdhocMaintenance={canLogAdhocMaintenance}
            onQuickAction={(action) => {
              if (action === 'add') {
                setShowJobForm(true);
              } else if (action === 'history') {
                setShowHistoryView(true);
              } else if (action === 'log-adhoc') {
                setLogMaintenanceError(null);
                setShowLogMaintenanceView(true);
              } else {
                onQuickAction(action);
              }
            }}
            onSyncRequirements={onSyncRequirements}
            hasUpdates={hasUpdates}
            checking={checking}
          />
        </div>
      </div>

      {/* Maintenance Check Modal */}
      <MaintenanceCheckModal
        isOpen={showCheckModal}
        onClose={() => {
          setShowCheckModal(false);
          setSelectedJob(null);
        }}
        job={selectedJob}
        equipment={equipment}
        onSave={handleSaveCompletion}
        onAddEquipment={(newEquipment) => {
          // FT-407: persist new equipment through the IDB-backed hook. Previously this
          // did setEquipment([...]) + storage.writeJSON() to localStorage — both dead
          // paths after the register moved to IndexedDB. addItem() writes to IDB via
          // useStorageState (which emits the sync events) and the hook re-renders us.
          addEquipmentItem(newEquipment);
        }}
        onViewHistory={handleViewHistory}
        onEdit={handleEditJob}  // FT-186: Pass edit handler to detail modal
        onDelete={async (jobId) => {
          setIsDeletingJob(true);
          try {
            // FT-353f: cascade FIRST — clear the linked defect's back-
            // reference (and revert status if safe) before the job leaves
            // the active list. handleCorrectiveJobDeleted is a no-op when
            // no defect back-references this job, so it's safe to call
            // unconditionally.
            const actor = currentUser?.name || currentUser?.email || 'Unknown';
            cascadeDefectOnJobDelete(jobId, actor);
            // FT-182 v10: Domain hook is SINGLE SOURCE OF TRUTH
            // archiveJob() atomically updates storage AND React state via useStorageState
            // No manual local state update needed - domain hook's list auto-excludes archived
            archiveJob(jobId);
          } finally {
            setIsDeletingJob(false);
          }
        }}
        isCompleting={isCompletingJob}
        isDeleting={isDeletingJob}
        completionHistory={
          selectedJob && Array.isArray(passedMaintenanceHistory)
            ? passedMaintenanceHistory
              .filter(h => h.jobId === selectedJob.id)
              .sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0))
            : []
        }
        vesselName={getCurrentVessel()?.name || ''}
        canSignOff={canSignMaintenance}
        onSignOff={handleSignOffCompletion}
        isSigningOff={isSigningOffCompletion}
      />

      {/* FT-267: LogMaintenanceView is rendered via the early-return block
          at the top of this component (mirrors AddJobView). Only the
          success/failure toast lives down here so it renders after the
          view closes. */}

      {/* FT-267: Success/failure toast for the ad-hoc maintenance log */}
      {logMaintenanceToast && (
        <div
          className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
            logMaintenanceToast.type === 'success'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          <div className="flex items-center gap-2">
            {logMaintenanceToast.type === 'success' ? (
              <Check className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            {logMaintenanceToast.message}
          </div>
        </div>
      )}

      {/* Add Custom Job Modal - FT-48: Now supports editing */}
      <AddCustomJobModal
        isOpen={showAddCustomModal}
        onClose={handleCloseAddModal}
        onSave={(jobData) => {
          // Track 1: instrument the save.
          try {
            track1.beginSave('PlannedMaintenanceTab.onSave', {
              hasEditingJob: !!editingJob,
              editingJobIdHash: editingJob ? track1.hashId(editingJob.id) : null,
              netState: track1.captureNetworkState(),
            });
            // [STALE-EDITING-JOB] - Investigator B's leading candidate.
            // If the array was replaced between handleEditJob and now, epoch
            // has bumped; a mismatch means editingJob.id may reference a
            // no-longer-existent record - the exact race B identified.
            if (editingJob && typeof editingJob.__track1_openEpoch === 'number') {
              const nowEpoch = track1.readHydrationEpoch();
              if (nowEpoch !== editingJob.__track1_openEpoch) {
                track1.log('[STALE-EDITING-JOB]', {
                  openedEpoch: editingJob.__track1_openEpoch,
                  saveEpoch: nowEpoch,
                  openedAt: editingJob.__track1_openedAt,
                  msElapsed: Date.now() - (editingJob.__track1_openedAt || 0),
                  editingJobIdHash: track1.hashId(editingJob.id),
                });
              }
            }
          } catch (_) { /* noop */ }

          try {
            if (editingJob) {
              domainUpdateJob(editingJob.id, { ...jobData, custom: true });
            } else {
              domainAddJob({ ...jobData, custom: true });
            }
          } finally {
            try { track1.endSave('ok'); } catch (_) { /* noop */ }
          }
        }}
        initialData={editingJob}
      />

      {/* Report Modal */}
      <MaintenanceReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        maintenanceJobs={filteredJobs}
        maintenanceHistory={passedMaintenanceHistory || {}}
      />

      {/* Settings View - Now full-screen, rendered above before main return */}

      {/* Mobile Summary Box - ONLY visible on mobile (<lg) */}
      <div className="lg:hidden mb-20 px-4">
        <MaintenanceSummaryCard stats={stats} />
      </div>

      {/* FT-358: mobile bottom-bar actions registered via
          useSetBottomBarActions above — the shell owns the bar. */}
    </>
  );
};

export default PlannedMaintenanceTab;